/* Keep each navigation word monochrome while adapting it to its own backdrop.
   No screenshots, image requests, render loop, or scroll-position changes. */
(() => {
  'use strict';
  const win = window, doc = win.document;
  if (!doc || typeof win.requestAnimationFrame !== 'function' || typeof win.cancelAnimationFrame !== 'function' ||
      typeof win.getComputedStyle !== 'function' || typeof doc.elementsFromPoint !== 'function') return;
  win.CueHeaderWordContrast?.destroy();

  const WORDS = '[data-nav-word], .logo-link, .header-cta .icon, #menu-toggle';
  const PERIOD = 80, THUMB = 96, CACHE_LIMIT = 24;
  // Bound synchronous work even in a long merchant-customized page. Browsers
  // with IntersectionObserver narrow the image supplement to the viewport.
  const READ_LIMIT = 512, IMAGE_LIMIT = 96, UNIT_LIMIT = 24, DEPTH_LIMIT = 32, THUMB_READ_LIMIT = 6;
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
  const number = value => Number.parseFloat(value) || 0;
  const transparent = () => [0, 0, 0, 0];
  const themeColor = theme => theme === 'light' ? [1, 1, 1, 1] : [0, 0, 0, 1];
  const validTheme = value => value === 'light' || value === 'dark';
  const over = (front, back) => {
    const a = front[3] + back[3] * (1 - front[3]);
    if (!a) return transparent();
    return [0, 1, 2].map(i => (front[i] * front[3] + back[i] * back[3] * (1 - front[3])) / a).concat(a);
  };
  const luminance = rgba => rgba.slice(0, 3).map(v => v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4)
    .reduce((sum, v, i) => sum + v * [.2126, .7152, .0722][i], 0);
  const color = value => {
    value = (value || '').trim().toLowerCase();
    if (value === 'transparent') return transparent();
    if (value === 'white') return themeColor('light');
    if (value === 'black') return themeColor('dark');
    if (/^#[\da-f]{3,8}$/i.test(value)) {
      let hex = value.slice(1);
      if (hex.length < 5) hex = [...hex].map(c => c + c).join('');
      if (hex.length === 6) hex += 'ff';
      if (hex.length === 8) return [0, 2, 4, 6].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
    }
    // Modern WebKit/Firefox may preserve CSS Color 4 coordinates rather than
    // serializing every computed color as rgb(). Convert common wide-gamut
    // colors to sRGB before measuring contrast; other color functions use the
    // section's declared theme below.
    const wide = value.match(/^color\((srgb|display-p3)\s+([^)]*)\)$/);
    if (wide) {
      const parts = wide[2].replace(/\//g, ' ').split(/\s+/).filter(Boolean);
      if (parts.length < 3) return null;
      let rgb = parts.slice(0, 3).map(v => number(v) / (v.endsWith('%') ? 100 : 1));
      if (wide[1] === 'display-p3') {
        const linear = rgb.map(v => v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4);
        rgb = [[1.224745, -.224904, 0], [-.042058, 1.042081, 0], [-.019642, -.078655, 1.098537]]
          .map(row => row.reduce((sum, v, i) => sum + v * linear[i], 0))
          .map(v => clamp(v <= .0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - .055));
      }
      return rgb.map(v => clamp(v)).concat(parts[3] ? clamp(number(parts[3]) / (parts[3].endsWith('%') ? 100 : 1)) : 1);
    }
    const match = value.match(/^rgba?\(([^)]+)\)$/);
    if (!match) return null;
    const parts = match[1].replace(/\//g, ' ').split(/[\s,]+/).filter(Boolean);
    if (parts.length < 3) return null;
    return parts.slice(0, 3).map(v => clamp(number(v) / (v.endsWith('%') ? 100 : 255)))
      .concat(parts[3] ? clamp(number(parts[3]) / (parts[3].endsWith('%') ? 100 : 1)) : 1);
  };
  const split = text => {
    let depth = 0, start = 0;
    const parts = [];
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '(') depth++;
      if (text[i] === ')') depth--;
      if (text[i] === ',' && depth === 0) { parts.push(text.slice(start, i).trim()); start = i + 1; }
    }
    parts.push(text.slice(start).trim());
    return parts;
  };
  function linearGradient(value, rect, x, y) {
    if (!/^linear-gradient\(/i.test(value)) return null;
    const parts = split(value.slice(value.indexOf('(') + 1, -1));
    let dx = 0, dy = 1;
    if (/^(to |[-.\d]+(?:deg|turn|rad))/.test(parts[0])) {
      const direction = parts.shift();
      if (direction.startsWith('to ')) {
        dx = direction.includes('right') ? 1 : direction.includes('left') ? -1 : 0;
        dy = direction.includes('bottom') ? 1 : direction.includes('top') ? -1 : 0;
        const length = Math.hypot(dx, dy); dx /= length; dy /= length;
      } else {
        const angle = number(direction) * (direction.endsWith('turn') ? Math.PI * 2 : direction.endsWith('rad') ? 1 : Math.PI / 180);
        dx = Math.sin(angle); dy = -Math.cos(angle);
      }
    }
    const length = Math.abs(rect.width * dx) + Math.abs(rect.height * dy) || 1;
    const at = clamp(((x - rect.left - rect.width / 2) * dx + (y - rect.top - rect.height / 2) * dy) / length + .5);
    const stops = parts.map(part => {
      const match = part.match(/^(rgba?\([^)]*\)|#[\da-f]{3,8}|transparent|white|black)(.*)$/i);
      if (!match) return null;
      const position = match[2].trim();
      return { rgba: color(match[1]), at: position ? number(position) / (position.endsWith('%') ? 100 : length) : null };
    });
    if (stops.length < 2 || stops.some(stop => !stop?.rgba)) return null;
    if (stops[0].at === null) stops[0].at = 0;
    if (stops[stops.length - 1].at === null) stops[stops.length - 1].at = 1;
    let last = 0;
    for (let i = 1; i < stops.length; i++) {
      if (stops[i].at === null) continue;
      stops[i].at = Math.max(stops[last].at, stops[i].at);
      for (let j = last + 1; j < i; j++) stops[j].at = stops[last].at + (stops[i].at - stops[last].at) * (j - last) / (i - last);
      last = i;
    }
    if (at <= stops[0].at) return stops[0].rgba;
    for (let i = 1; i < stops.length; i++) if (at <= stops[i].at) {
      const a = stops[i - 1], b = stops[i], t = (at - a.at) / (b.at - a.at || 1);
      // Premultiplied interpolation prevents a transparent stop from adding a
      // dark fringe to otherwise light masks.
      const alpha = a.rgba[3] * (1 - t) + b.rgba[3] * t;
      return [0, 1, 2].map(c => alpha ? (a.rgba[c] * a.rgba[3] * (1 - t) + b.rgba[c] * b.rgba[3] * t) / alpha : 0).concat(alpha);
    }
    return stops[stops.length - 1].rgba;
  }

  let instance = null, destroyed = false;
  const managerListeners = [];
  const listen = (list, target, type, fn, options) => {
    target.addEventListener(type, fn, options);
    list.push(() => target.removeEventListener(type, fn, options));
  };
  const mount = () => {
    if (destroyed) return;
    const header = doc.getElementById('site-header');
    if (instance?.header === header) { instance.refresh(); return; }
    instance?.destroy(); instance = null;
    if (!doc.body || !header || header.getAttribute('data-header-style') !== 'floating' || header.getAttribute('data-header-blend') !== 'words') return;
    instance = create(header);
  };

  function create(header) {
    let alive = true, frame = 0, timer = 0, lastRead = -PERIOD, collect = true;
    let units = [], images = [], imageObserver = null, imageObserverReady = false, thumbnailReads = 0;
    let failedThumbnails = new WeakMap();
    const watchedImages = new Set(), intersectingImages = new Set();
    const listeners = [], observers = [], originals = new Map(), thumbnails = new Map();
    const now = () => win.performance?.now?.() ?? Date.now();
    const cancel = () => {
      if (frame) win.cancelAnimationFrame(frame);
      if (timer) win.clearTimeout(timer);
      frame = timer = 0;
    };
    const refresh = () => { collect = true; schedule(); };
    function schedule() {
      if (!alive || doc.hidden || frame || timer) return;
      const wait = Math.max(0, PERIOD - (now() - lastRead));
      if (wait) timer = win.setTimeout(() => { timer = 0; if (alive && !doc.hidden) frame = win.requestAnimationFrame(render); }, wait);
      else frame = win.requestAnimationFrame(render);
    }
    const corsCopies = new Map(), copyAttempts = new Map();
    const COPY_ATTEMPT_LIMIT = 2;
    // Async CORS upgrade for canvas-tainted images: sections declare one
    // data-header-theme for the whole band, so a locally dark photo (e.g. the
    // frames portrait card) cannot be seen through the declared fallback.
    // The Shopify CDN serves Access-Control-Allow-Origin:* on direct https
    // URLs (the http→https redirect answer carries no CORS header, so the
    // https URL is loaded directly). Rendering keeps using the sync path;
    // when a clean copy arrives the failed marks are cleared and the words
    // re-sample. Bounded by COPY_ATTEMPT_LIMIT per source.
    function maybeFetchCleanCopy(source) {
      if (!source || corsCopies.has(source)) return;
      const attempts = copyAttempts.get(source) || 0;
      if (attempts >= COPY_ATTEMPT_LIMIT) return;
      copyAttempts.set(source, attempts + 1);
      let probe;
      try { probe = new Image(); } catch (_) { return; }
      probe.crossOrigin = 'anonymous';
      const cleanSource = source.replace(/^http:\/\//i, 'https://');
      let settled = false;
      const finish = ok => {
        if (settled) return;
        settled = true;
        if (!alive || !ok) return;
        corsCopies.set(source, probe);
        thumbnails.delete(source);
        failedThumbnails = new WeakMap();
        schedule();
      };
      probe.onload = () => finish(true);
      probe.onerror = () => finish(false);
      probe.src = cleanSource;
    }
    function thumbnail(img) {
      if (!img.complete || !img.naturalWidth || !img.naturalHeight) return null;
      const source = img.currentSrc || img.src;
      if (!source) return null;
      if (thumbnails.has(source)) return thumbnails.get(source);
      const clean = corsCopies.get(source);
      // A previously-failed image only short-circuits while no clean copy
      // exists — and retries the CORS upgrade (bounded per source).
      if (failedThumbnails.get(img) === source && !clean) {
        maybeFetchCleanCopy(source);
        return null;
      }
      if (thumbnailReads >= THUMB_READ_LIMIT) return null;
      thumbnailReads++;
      let result = null;
      try {
        // Draw the already-loaded responsive image; use the clean CORS copy
        // once one has arrived. Never alter the live img's src/crossOrigin.
        const drawable = clean && clean.complete && clean.naturalWidth ? clean : img;
        const scale = Math.min(1, THUMB / Math.max(drawable.naturalWidth, drawable.naturalHeight));
        const canvas = doc.createElement('canvas');
        canvas.width = Math.max(1, Math.round(drawable.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(drawable.naturalHeight * scale));
        let ctx;
        try { ctx = canvas.getContext('2d', { willReadFrequently: true }); }
        catch (_) { ctx = canvas.getContext('2d'); }
        if (ctx) {
          ctx.drawImage(drawable, 0, 0, canvas.width, canvas.height);
          result = { pixels: ctx.getImageData(0, 0, canvas.width, canvas.height).data, width: canvas.width, height: canvas.height };
        }
      } catch (_) { /* file/CORS restrictions use the nearest section theme. */ }
      // A tainted element must not poison a readable element with the same URL
      // (e.g. Shopify CDN image inserted with a different crossorigin mode).
      if (result) {
        if (thumbnails.size >= CACHE_LIMIT) thumbnails.delete(thumbnails.keys().next().value);
        thumbnails.set(source, result);
      } else {
        failedThumbnails.set(img, source);
        maybeFetchCleanCopy(source);
      }
      return result;
    }
    function render() {
      frame = 0;
      if (!alive || doc.hidden || !header.isConnected || win.innerWidth <= 0 || win.innerHeight <= 0) return;
      lastRead = now(); thumbnailReads = 0;
      if (header.getAttribute('data-header-style') !== 'floating' || header.getAttribute('data-header-blend') !== 'words') { restore(); return; }
      if (collect) {
        units = [...header.querySelectorAll(WORDS)].slice(0, UNIT_LIMIT);
        images = [...(doc.images || [])].filter(img => !header.contains(img));
        if (imageObserver) {
          const current = new Set(images);
          watchedImages.forEach(img => {
            if (!current.has(img)) { imageObserver.unobserve(img); watchedImages.delete(img); intersectingImages.delete(img); }
          });
          images.forEach(img => { if (!watchedImages.has(img)) { watchedImages.add(img); imageObserver.observe(img); } });
        }
        collect = false;
      }
      let reads = 0;
      const exhausted = {};
      const read = fn => { if (reads >= READ_LIMIT) throw exhausted; reads++; return fn(); };
      const safely = (fn, otherwise = null) => { try { return fn(); } catch (_) { return otherwise; } };
      const styles = new Map(), rects = new Map(), pseudos = new Map(), imageBoxes = new Map();
      const style = node => {
        if (!styles.has(node)) styles.set(node, read(() => win.getComputedStyle(node)));
        return styles.get(node);
      };
      const rect = node => {
        if (!rects.has(node)) rects.set(node, read(() => node.getBoundingClientRect()));
        return rects.get(node);
      };
      const inside = (r, x, y) => r.width > 0 && r.height > 0 && x >= r.left && x < r.right && y >= r.top && y < r.bottom;
      const visible = node => {
        let depth = 0;
        for (let p = node; p && p.nodeType === 1 && depth++ < DEPTH_LIMIT; p = p.parentElement) {
          const s = style(p);
          if (s.display === 'none' || /^(hidden|collapse)$/.test(s.visibility) || s.contentVisibility === 'hidden' || Number(s.opacity) === 0) return false;
        }
        return true;
      };
      const fallback = node => {
        let depth = 0;
        for (let p = node; p && depth++ < DEPTH_LIMIT; p = p.parentElement) {
          const value = p.getAttribute?.('data-header-theme');
          if (validTheme(value)) return value;
        }
        return validTheme(header.getAttribute('data-theme')) ? header.getAttribute('data-theme') : 'dark';
      };
      const menuTheme = fallback(null), menuOpen = doc.body.classList.contains('menu-open');
      const paints = (s, r, x, y, theme) => {
        let rgba = color(s.backgroundColor);
        if (!rgba) {
          rgba = s.backgroundColor ? themeColor(theme) : transparent();
          // Preserve explicit transparency when an unsupported color function
          // (lab/oklch or a future syntax) uses a declared fallback theme.
          const alpha = (s.backgroundColor || '').match(/\/\s*([\d.]+%?)\s*\)$/);
          if (alpha) rgba[3] = clamp(number(alpha[1]) / (alpha[1].endsWith('%') ? 100 : 1));
        }
        const layers = split(s.backgroundImage || 'none');
        for (let i = layers.length - 1; i >= 0; i--) {
          if (layers[i] === 'none') continue;
          const gradient = linearGradient(layers[i], r, x, y);
          if (gradient) rgba = over(gradient, rgba);
          // CSS URL/radial/repeating backgrounds, filters, masks, blend modes
          // and 3D transforms are not rasterized. A declared section theme is
          // the stable fallback; no expensive or extra-network emulation.
          else if (!rgba[3]) rgba = themeColor(theme);
        }
        return rgba;
      };
      const imagePixel = (img, x, y) => {
        const s = style(img), r = rect(img), pixels = thumbnail(img);
        if (!pixels) return themeColor(fallback(img));
        let flipX = false, flipY = false;
        let depth = 0;
        for (let p = img; p && p !== doc.body && depth++ < DEPTH_LIMIT; p = p.parentElement) {
          const transform = style(p).transform;
          if (!transform || transform === 'none') continue;
          const m = transform.match(/^matrix(3d)?\(([^)]+)\)$/);
          if (!m) return themeColor(fallback(img));
          const v = m[2].split(',').map(Number);
          if (m[1]) {
            if ([1, 2, 3, 4, 6, 7, 8, 9, 11, 14].some(i => Math.abs(v[i]) > .001)) return themeColor(fallback(img));
            v[3] = v[5];
          } else if (Math.abs(v[1]) > .001 || Math.abs(v[2]) > .001) return themeColor(fallback(img));
          if (v[0] < 0) flipX = !flipX;
          if (v[3] < 0) flipY = !flipY;
        }
        if (!imageBoxes.has(img)) imageBoxes.set(img, [read(() => img.offsetWidth) || r.width, read(() => img.offsetHeight) || r.height]);
        const [boxWidth, boxHeight] = imageBoxes.get(img);
        const scaleX = r.width / boxWidth, scaleY = r.height / boxHeight;
        const left = number(s.borderLeftWidth) + number(s.paddingLeft), right = number(s.borderRightWidth) + number(s.paddingRight);
        const top = number(s.borderTopWidth) + number(s.paddingTop), bottom = number(s.borderBottomWidth) + number(s.paddingBottom);
        const width = boxWidth - left - right, height = boxHeight - top - bottom;
        if (width <= 0 || height <= 0 || scaleX <= 0 || scaleY <= 0) return transparent();
        const px = (flipX ? r.right - x : x - r.left) / scaleX - left;
        const py = (flipY ? r.bottom - y : y - r.top) / scaleY - top;
        const iw = img.naturalWidth, ih = img.naturalHeight;
        let sw = width / iw, sh = height / ih;
        if (s.objectFit !== 'fill') {
          const fit = s.objectFit === 'cover' ? Math.max(sw, sh) : s.objectFit === 'none' ? 1 : Math.min(sw, sh);
          sw = sh = s.objectFit === 'scale-down' ? Math.min(1, fit) : fit;
        }
        const renderedWidth = iw * sw, renderedHeight = ih * sh;
        const positions = (s.objectPosition || '50% 50%').trim().split(/\s+/);
        // Ordinary two-value object-position covers this site's assets. The
        // uncommon four-value edge-offset syntax falls back to center.
        const offset = (token, space, axis) => {
          if (!token || token === 'center' || positions.length > 2) return space / 2;
          if (token === (axis === 'x' ? 'left' : 'top')) return 0;
          if (token === (axis === 'x' ? 'right' : 'bottom')) return space;
          return token.endsWith('%') ? space * number(token) / 100 : number(token);
        };
        const u = (px - offset(positions[0], width - renderedWidth, 'x')) / renderedWidth;
        const v = (py - offset(positions[1] || '50%', height - renderedHeight, 'y')) / renderedHeight;
        if (u < 0 || u > 1 || v < 0 || v > 1) return transparent();
        const i = (Math.min(pixels.height - 1, Math.floor(v * pixels.height)) * pixels.width + Math.min(pixels.width - 1, Math.floor(u * pixels.width))) * 4;
        return [0, 1, 2, 3].map(c => pixels.pixels[i + c] / 255);
      };
      const unitsRead = units.map(unit => safely(() => ({unit, rect: rect(unit)}))).filter(item => item && safely(() => {
        const {unit, rect: r} = item;
        return r.width && r.height && r.right > 0 && r.left < win.innerWidth && r.bottom > 0 && r.top < win.innerHeight && visible(unit);
      }, false));
      if (!unitsRead.length) return;
      const minY = Math.min(...unitsRead.map(item => item.rect.top)), maxY = Math.max(...unitsRead.map(item => item.rect.bottom));
      // A section marked data-header-uniform (the hero) keeps the whole bar in
      // one ink color — the section's declared theme — instead of per-word sampling.
      let uniformTheme = null;
      if (!menuOpen) {
        const bandY = clamp((minY + maxY) / 2, 0, win.innerHeight - 1);
        const stack = safely(() => read(() => doc.elementsFromPoint(win.innerWidth / 2, bandY)), []) || [];
        for (const node of stack) {
          const uniform = !header.contains(node) && node.closest?.('[data-header-uniform]');
          if (!uniform) continue;
          let depth = 0;
          for (let p = uniform; p && depth++ < DEPTH_LIMIT; p = p.parentElement) {
            const value = p.getAttribute?.('data-header-theme');
            if (validTheme(value)) { uniformTheme = value; break; }
          }
          if (!uniformTheme) uniformTheme = menuTheme;
          break;
        }
      }
      // Pointer-events:none artwork is omitted by hit testing. Only visible
      // images intersecting the navigation band need the bounded supplement.
      const imageCandidates = imageObserverReady ? [...intersectingImages] : images;
      const passiveImages = menuOpen ? [] : imageCandidates.slice(0, IMAGE_LIMIT).filter(img => safely(() => {
        const r = rect(img);
        return r.bottom > minY && r.top < maxY && r.right > 0 && r.left < win.innerWidth && style(img).pointerEvents === 'none' && visible(img);
      }, false));
      function sample(x, y) {
        const stack = read(() => doc.elementsFromPoint(x, y)).slice(0, DEPTH_LIMIT)
          .filter(node => !header.contains(node) && !node.closest?.('#mobile-menu') && safely(() => visible(node), false));
        for (const img of [...passiveImages].reverse()) {
          if (stack.includes(img) || !inside(rect(img), x, y)) continue;
          let clipped = false;
          let depth = 0;
          for (let p = img.parentElement; p && p !== doc.body && depth++ < DEPTH_LIMIT; p = p.parentElement) {
            const s = style(p), r = rect(p);
            if ((/(hidden|clip|scroll|auto)/.test(s.overflowX) && (x < r.left || x >= r.right)) ||
                (/(hidden|clip|scroll|auto)/.test(s.overflowY) && (y < r.top || y >= r.bottom))) { clipped = true; break; }
          }
          if (clipped) continue;
          const index = stack.findIndex(node => node.contains(img));
          if (index >= 0) stack.splice(index, 0, img);
        }
        const theme = fallback(stack[0]);
        // Build tiny paint groups from the hit stack. Apply opacity once to
        // each group, so a translucent image + parent background isn't counted
        // twice. Child order comes from actual painted hit order, not offsets.
        const records = new Map();
        const record = node => {
          if (!records.has(node)) records.set(node, {node, rank: Infinity, children: []});
          return records.get(node);
        };
        stack.forEach((node, index) => {
          let depth = 0;
          for (let p = node; p && p.nodeType === 1 && depth++ < DEPTH_LIMIT; p = p.parentElement) record(p).rank = Math.min(record(p).rank, index);
        });
        const roots = [];
        records.forEach(item => {
          const parent = records.get(item.node.parentElement);
          (parent ? parent.children : roots).push(item);
        });
        const paint = item => {
          const node = item.node, s = style(node), r = rect(node);
          const covers = inside(r, x, y);
          let rgba = covers ? paints(s, r, x, y, fallback(node)) : transparent();
          if (covers && node.tagName === 'IMG') rgba = over(imagePixel(node, x, y), rgba);
          if (covers && (node.tagName === 'VIDEO' || node.tagName === 'CANVAS')) rgba = themeColor(fallback(node));
          item.children.sort((a, b) => b.rank - a.rank).forEach(child => { rgba = over(paint(child), rgba); });
          // Only full-box, absolute pseudo masks are supported. Text, borders,
          // blur, complex clipping and arbitrary pseudo geometry aren't sampled.
          if (!pseudos.has(node)) pseudos.set(node, ['::before', '::after'].map(p => safely(() => read(() => win.getComputedStyle(node, p)))));
          for (const mask of pseudos.get(node)) {
            if (covers && mask && mask.display !== 'none' && !/^(hidden|collapse)$/.test(mask.visibility) && mask.content && mask.content !== 'none' && mask.content !== 'normal' && mask.position === 'absolute' &&
                ['top', 'right', 'bottom', 'left'].every(edge => mask[edge] === '0px')) {
              const tint = paints(mask, r, x, y, fallback(node)); tint[3] *= Number(mask.opacity || 1); rgba = over(tint, rgba);
            }
          }
          rgba[3] *= Number(s.opacity || 1);
          return rgba;
        };
        let rgba = themeColor(theme);
        roots.sort((a, b) => b.rank - a.rank).forEach(root => { rgba = over(paint(root), rgba); });
        return luminance(rgba);
      }
      const changes = unitsRead.map(({unit, rect: r}) => {
        if (menuOpen) return {unit, theme: menuTheme};
        if (uniformTheme) return {unit, theme: uniformTheme};
        const points = r.width < 28 ? [.2, .5, .8] : [.12, .31, .5, .69, .88];
        const y = clamp(r.top + r.height / 2, 0, win.innerHeight - 1);
        const mean = safely(() => points.reduce((sum, t) => sum + sample(clamp(r.left + r.width * t, 0, win.innerWidth - 1), y), 0) / points.length);
        // A layout/read failure or an exhausted budget affects this word only.
        // Section/header declarations remain a deterministic readable fallback.
        if (mean === null) return {unit, theme: menuTheme};
        const previous = unit.getAttribute('data-word-theme');
        const threshold = previous === 'dark' ? .215 : previous === 'light' ? .145 : .18;
        return {unit, theme: mean >= threshold ? 'light' : 'dark'};
      });
      // Finish all layout/style/image reads before changing any rendered word.
      changes.forEach(({unit, theme}) => {
        if (!originals.has(unit)) originals.set(unit, unit.getAttribute('data-word-theme'));
        if (unit.getAttribute('data-word-theme') !== theme) unit.setAttribute('data-word-theme', theme);
      });
    }
    listen(listeners, win, 'scroll', schedule, {passive: true});
    listen(listeners, win, 'resize', refresh, {passive: true});
    listen(listeners, doc, 'cue:scene-layout', refresh);
    listen(listeners, doc, 'transitionend', schedule);
    listen(listeners, doc, 'animationend', schedule);
    listen(listeners, doc, 'load', event => { if (event.target?.tagName === 'IMG') { thumbnails.delete(event.target.currentSrc || event.target.src); failedThumbnails.delete(event.target); refresh(); } }, true);
    listen(listeners, doc, 'error', event => { if (event.target?.tagName === 'IMG') refresh(); }, true);
    listen(listeners, doc, 'visibilitychange', () => doc.hidden ? cancel() : refresh());
    if (win.visualViewport) {
      listen(listeners, win.visualViewport, 'resize', refresh, {passive: true});
      listen(listeners, win.visualViewport, 'scroll', schedule, {passive: true});
    }
    if (doc.fonts) {
      doc.fonts.ready?.then?.(() => { if (alive) refresh(); }, () => {});
      if (doc.fonts.addEventListener) listen(listeners, doc.fonts, 'loadingdone', refresh);
    }
    if (win.MutationObserver) {
      const content = new win.MutationObserver(refresh);
      content.observe(doc.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['data-header-theme']});
      const controls = new win.MutationObserver(schedule);
      controls.observe(header, {attributes: true, attributeFilter: ['data-theme', 'data-header-style', 'data-header-blend']});
      controls.observe(doc.body, {attributes: true, attributeFilter: ['class']});
      observers.push(content, controls);
    }
    if (win.IntersectionObserver) {
      imageObserver = safelyCreateObserver();
      function safelyCreateObserver() {
        try {
          const observer = new win.IntersectionObserver(entries => {
            if (!alive) return;
            imageObserverReady = true;
            entries.forEach(entry => entry.isIntersecting ? intersectingImages.add(entry.target) : intersectingImages.delete(entry.target));
            schedule();
          });
          observers.push(observer); return observer;
        } catch (_) { return null; }
      }
    }
    if (win.ResizeObserver) {
      const resize = new win.ResizeObserver(refresh); resize.observe(header); observers.push(resize);
    }
    function restore() {
      originals.forEach((value, unit) => value === null ? unit.removeAttribute('data-word-theme') : unit.setAttribute('data-word-theme', value));
      originals.clear();
    }
    schedule();
    return {header, refresh, destroy() {
      if (!alive) return;
      alive = false; cancel(); listeners.forEach(remove => remove()); observers.forEach(observer => observer.disconnect());
      thumbnails.clear(); watchedImages.clear(); intersectingImages.clear(); failedThumbnails = new WeakMap(); corsCopies.clear(); copyAttempts.clear();
      restore(); units = images = [];
    }};
  }

  listen(managerListeners, doc, 'shopify:section:load', mount);
  listen(managerListeners, doc, 'shopify:section:unload', event => {
    if (instance && (event.target === instance.header || event.target?.contains?.(instance.header))) { instance.destroy(); instance = null; }
  });
  listen(managerListeners, win, 'pagehide', event => {
    if (event.persisted) { instance?.destroy(); instance = null; }
    else destroy();
  });
  listen(managerListeners, win, 'pageshow', mount);
  function destroy() {
    if (destroyed) return;
    destroyed = true; instance?.destroy(); instance = null; managerListeners.forEach(remove => remove());
  }
  win.CueHeaderWordContrast = {refresh: mount, destroy};
  if (doc.readyState === 'loading') listen(managerListeners, doc, 'DOMContentLoaded', mount, {once: true});
  else mount();
})();
